import { useEffect, useRef, useState } from "react";

interface EmotionPrediction {
	emotions: { name: string; score: number }[];
}

interface UseRealtimeAudioEmotionProps {
	apiKey: string;
	modelName: string;
	recordingLengthMs?: number;
	streamWindowLengthMs?: number;
	isCleared?: boolean;
}

export const blobToBase64 = (blob: Blob): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onloadend = () => {
			if (!reader.result) {
				reject(new Error("Failed to read blob"));
				return;
			}

			const base64String = reader.result as string;
			// Remove the data URL prefix (e.g., "data:audio/wav;base64,")
			const base64Data = base64String.split(",")[1];
			resolve(base64Data);
		};

		reader.onerror = () => {
			reject(new Error("Error reading blob"));
		};

		reader.readAsDataURL(blob);
	});
};

function useRealtimeAudioEmotion({
	apiKey,
	modelName,
	streamWindowLengthMs = 5000,
	isCleared,
}: UseRealtimeAudioEmotionProps) {
	const socketRef = useRef<WebSocket | null>(null);
	const mountRef = useRef(true);
	const numReconnects = useRef(0);
	const maxReconnects = 3;
	const [predictions, setPredictions] = useState<EmotionPrediction[] | string>(
		[],
	);
	const [status, setStatus] = useState<string>("");

	useEffect(() => {
		mountRef.current = true;
		connect();

		return () => {
			mountRef.current = false;
			stopEverything();
		};
	}, []);

	useEffect(() => {
		if (isCleared) {
			setPredictions([]);
		}
	}, [isCleared]);

	function connect() {
		const socketUrl = `wss://api.hume.ai/v0/stream/models?apikey=${apiKey}`;
		setStatus("Connecting to server...");
		socketRef.current = new WebSocket(socketUrl);

		socketRef.current.onopen = handleSocketOpen;
		socketRef.current.onmessage = handleSocketMessage;
		socketRef.current.onclose = handleSocketClose;
		socketRef.current.onerror = handleSocketError;
	}

	function handleSocketOpen() {
		setStatus("Connected to WebSocket");
	}

	function handleSocketMessage(event: MessageEvent) {
		setStatus("");
		const response = JSON.parse(event.data);

		if (response.error) {
			console.error("API Error:", response.error);
			setStatus(response.error);
			return;
		}

		const newPredictions: EmotionPrediction[] | string =
			response[modelName]?.predictions || response[modelName]?.warning || [];
		setPredictions(newPredictions);

		if (Array.isArray(newPredictions) && newPredictions.length === 0) {
			console.warn(
				"No emotion data received. Check API response and audio quality.",
			);
		}
	}

	function handleSocketClose() {
		if (mountRef.current && numReconnects.current < maxReconnects) {
			setStatus("Reconnecting...");
			numReconnects.current++;
			connect();
		} else {
			setStatus("Failed to connect after multiple attempts");
			stopEverything();
		}
	}

	function handleSocketError(event: Event) {
		console.error("WebSocket Error: ", event);
		if (numReconnects.current > maxReconnects) {
			setStatus("Failed to connect after multiple attempts");
			stopEverything();
		} else {
			numReconnects.current++;
			connect();
		}
	}

	function stopEverything() {
		if (socketRef.current) {
			socketRef.current.close();
			socketRef.current = null;
		}
	}

	function sendAudio(blob: string) {
		if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
			return;
		}

		try {
			const requestData = {
				data: blob,
				models: { [modelName]: {} },
				stream_window_ms: streamWindowLengthMs,
			};
			socketRef.current.send(JSON.stringify(requestData));
		} catch (error) {
			console.error("Error converting blob to base64:", error);
		}
	}

	return { predictions, status, sendAudio };
}

export default useRealtimeAudioEmotion;
